/* eslint-disable jsx-a11y/iframe-has-title */
import React, { useState } from 'react';
import { PlayCircle, GraduationCap, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const modules = [
    { id: 1, title: "Dashboard Overview", url: "https://www.youtube.com/embed/YjdwT31HxaA", duration: "2:15" },
    { id: 2, title: "Creating a New Case", url: "https://www.youtube.com/embed/XsI2txeqOaA", duration: "3:40" },
    { id: 3, title: "Uploading Documents", url: "https://www.youtube.com/embed/yT6omjC-NKo", duration: "1:55" },
    { id: 4, title: "Using the A.I Assistant", url: "https://www.youtube.com/embed/YjdwT31HxaA", duration: "4:20" },
    { id: 5, title: "Team Collaboration", url: "https://www.youtube.com/embed/XsI2txeqOaA", duration: "2:30" },
    { id: 6, title: "Generating Reports", url: "https://www.youtube.com/embed/yT6omjC-NKo", duration: "3:10" },
    { id: 7, title: "Managing P&L", url: "https://www.youtube.com/embed/YjdwT31HxaA", duration: "5:00" },
    { id: 8, title: "Document Analysis", url: "https://www.youtube.com/embed/XsI2txeqOaA", duration: "2:45" },
    { id: 9, title: "Borrower Experience", url: "https://www.youtube.com/embed/yT6omjC-NKo", duration: "3:30" },
    { id: 10, title: "Advanced Settings", url: "https://www.youtube.com/embed/YjdwT31HxaA", duration: "4:00" }
];

const TrainingPortal = () => {
    const [activeModuleId, setActiveModuleId] = useState(1);
    const activeModule = modules.find(m => m.id === activeModuleId) || modules[0];

    const handleNext = () => {
        const nextId = activeModuleId + 1;
        if (nextId <= modules.length) setActiveModuleId(nextId);
    };

    return (
        <div className="flex flex-col h-full dark:bg-slate-950 bg-slate-50 dark:text-slate-200 text-slate-900">
            {/* Header */}
            <div className="px-8 py-6 border-b dark:border-slate-800 border-slate-200 flex items-center gap-4 dark:bg-slate-900/50 bg-white/50 backdrop-blur-sm sticky top-0 z-10">
                <div className="p-3 bg-sky-500/10 rounded-xl text-sky-400 border border-sky-500/20">
                    <GraduationCap size={28} />
                </div>
                <div>
                    <h1 className="text-2xl font-bold dark:text-white text-slate-900">Lender Training Portal</h1>
                    <p className="dark:text-slate-400 text-slate-500 text-sm">Master the platform with our step-by-step video guides.</p>
                </div>
            </div>

            <div className="flex flex-col md:flex-row flex-1 overflow-hidden">
                {/* Sidebar - Table of Contents */}
                <div className="w-full md:w-80 border-r dark:border-slate-800 border-slate-200 overflow-y-auto dark:bg-slate-900/30 bg-slate-100">
                    <div className="p-4">
                        <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-4 px-2">Course Content</h2>
                        <div className="space-y-1">
                            {modules.map(module => (
                                <button
                                    key={module.id}
                                    onClick={() => setActiveModuleId(module.id)}
                                    className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl text-left transition-all duration-200 ${activeModuleId === module.id
                                        ? "bg-sky-500/10 border border-sky-500/20 shadow-lg shadow-sky-900/10"
                                        : "hover:bg-slate-200 dark:hover:bg-slate-800 border border-transparent"
                                        }`}
                                >
                                    <div className={`
                        min-w-[24px] h-6 flex items-center justify-center rounded-full text-xs font-bold ring-1 transition-colors
                        ${activeModuleId === module.id ? "bg-sky-500 text-white ring-sky-400" : "dark:bg-slate-800 bg-slate-200 dark:text-slate-500 text-slate-600 dark:ring-slate-700 ring-slate-300"}
                  `}>
                                        {module.id}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className={`text-sm font-medium truncate ${activeModuleId === module.id ? "dark:text-sky-100 text-sky-900" : "dark:text-slate-400 text-slate-600"}`}>
                                            {module.title}
                                        </p>
                                        <p className="text-xs text-slate-500 mt-0.5">{module.duration}</p>
                                    </div>
                                    {activeModuleId === module.id && <ChevronRight size={14} className="text-sky-400" />}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Main Content - Video */}
                <div className="flex-1 overflow-y-auto p-4 md:p-8 dark:bg-slate-950 bg-slate-50">
                    <div className="max-w-4xl mx-auto">
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={activeModule.id}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                transition={{ duration: 0.3 }}
                            >
                                <div className="aspect-video w-full rounded-2xl overflow-hidden shadow-2xl shadow-sky-900/20 border border-slate-800 bg-black relative group">
                                    <iframe
                                        width="100%"
                                        height="100%"
                                        src={activeModule.url}
                                        title={activeModule.title}
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                        allowFullScreen
                                        className="absolute inset-0"
                                    />
                                </div>

                                <div className="mt-8 flex flex-col md:flex-row items-start gap-6">
                                    <div className={`p-4 rounded-xl hidden md:block ${activeModule.id % 2 === 0 ? "bg-indigo-500/10 text-indigo-400" : "bg-emerald-500/10 text-emerald-400"
                                        }`}>
                                        <PlayCircle size={32} />
                                    </div>
                                    <div>
                                        <h2 className="text-3xl font-bold dark:text-white text-slate-900 mb-2">{activeModule.title}</h2>
                                        <p className="dark:text-slate-400 text-slate-600 leading-relaxed text-lg">
                                            In this module, you will learn about <strong>{activeModule.title}</strong> and how to leverage it specific tools to streamline your process.
                                        </p>

                                        <div className="mt-8 flex items-center gap-4">
                                            {activeModuleId < modules.length && (
                                                <button
                                                    onClick={handleNext}
                                                    className="px-6 py-2.5 bg-sky-600 hover:bg-sky-500 text-white font-semibold rounded-lg transition-colors shadow-lg shadow-sky-900/20 flex items-center gap-2"
                                                >
                                                    Next Lesson <ChevronRight size={16} />
                                                </button>
                                            )}

                                            <button className="px-6 py-2.5 border dark:border-slate-700 border-slate-300 hover:border-slate-500 dark:hover:border-slate-500 dark:text-slate-300 text-slate-600 font-semibold rounded-lg transition-colors">
                                                Mark as Complete
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        </AnimatePresence>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TrainingPortal;
